/* eslint-disable max-lines */
import { degreesToRadians } from '@deltasierra/utilities-math';
import { getEnumValues } from '@deltasierra/utilities-object';
import { isNullOrUndefined, pick } from '@deltasierra/type-utilities';
import { IRenderContext } from '../graphics/renderContext';
import { IPoint, IRectangle, ISize, RectangleCorner, scale, swapWidthAndHeight } from '../geometry/geometry2d';
import { AffineTransform } from '../common/lang/matrix';
import { BuilderFontCustomDto } from '../builderFont/BuilderFontCustom';
import { BuilderDocument, getBleedAmount, isMultiImageContentDocument, isPrintDocument } from './builderDocument';
import {
    Layer,
    ImageLayer,
    TextLayer,
    VideoLayer,
    MapLayer,
    isImageLayer,
    isMapLayer,
    isTextLayer,
    isVideoLayer,
    LayerType,
    TextFit,
} from './layers';
import { convertUnits, PrintUnits, isPageRotated } from './print';
import { TextRendererV1, TextRendererV2 } from './textRendering';
import { BuilderDocumentFormat } from './builderDocumentFormat';
import { OverlaySupportedLayer } from './placeholderOverlayRenderer';

export type TextToFallbackFontLink = {
    fallbackFont: string;
    selectedFont: string;
    text: string;
};

export interface LoadedVideo {
    element: HTMLVideoElement;
    frame: HTMLCanvasElement | HTMLVideoElement;
    orientation?: VideoOrientation;
    preferredTransform?: AffineTransform;
    hasPlayedAtLeastOnce?: boolean; // DSMA-108: hack, iOS renders first frame okay but others are not rotated correctly
}

export enum VideoOrientation {
    Rotation0 = 0,
    Rotation90 = 90,
}

export type RenderPlaceholderOverlayCallback = (ctx: IRenderContext, layer: OverlaySupportedLayer) => void;

export interface IRenderDocumentOptions {
    document: BuilderDocument;
    context: IRenderContext;

    margin: number;
    includeBleed: boolean;
    includeTrimMarks: boolean;
    clipToTrimBox: boolean;
    globalTransform: {
        scale: IPoint;
    };
    trimTransform: {
        scale: IPoint;
    };
    outputDimensions?: {
        width: number;
        height: number;
    };

    bleedScale: number;
    pageIndex?: number;
    multiImageIndex?: number;
    replaceTransparentWithWhite?: boolean;

    imageLayerToImageSource: (layer: ImageLayer) => any;
    videoLayerToVideoSource: (layer: VideoLayer) => LoadedVideo;
    renderPlaceHolderOverlay?: RenderPlaceholderOverlayCallback;

    substituteText: (text: string) => string;
    asVideoOverlay?: boolean;
    useOldTextRenderingLineHeightFix: boolean;
    useTextRenderingLineHeightFix: boolean;
    customFonts: BuilderFontCustomDto[];
    textToFallbackFontLink?: TextToFallbackFontLink[];
}

export type IRenderDocumentForScreenOptions = Pick<
    IRenderDocumentOptions,
    | 'asVideoOverlay'
    | 'clipToTrimBox'
    | 'imageLayerToImageSource'
    | 'multiImageIndex'
    | 'pageIndex'
    | 'renderPlaceHolderOverlay'
    | 'replaceTransparentWithWhite'
    | 'substituteText'
    | 'videoLayerToVideoSource'
> & {
    dimensions?: ISize;
};

export type ICalculateSizesOptions = Pick<Partial<IRenderDocumentForScreenOptions>, 'dimensions' | 'pageIndex'> &
    Pick<Partial<IRenderDocumentOptions>, 'bleedScale'>;

export interface ICalculateSizesResult {
    bleedScale: number;
    canvasSize: ISize;
    globalScale: IPoint;
}

enum TrimLineOrientation {
    horizontal = 'horizontal',
    vertical = 'vertical',
}

interface ITrimLineRectsOptions {
    pageSize: ISize;
    margin: number;
    trim: number;
    trimLineLength: number;
    trimLineWidth: number;
}

export class BuilderDocumentRenderer {
    private static RGB_WHITE = 'rgb(255, 255, 255)';

    private static RGBA_TRANSPARENT = 'rgba(255, 255, 255, 0)';

    private textRenderer: {
        v1: TextRendererV1;
        v2: TextRendererV2;
    };

    public constructor() {
        this.textRenderer = {
            v1: new TextRendererV1(),
            v2: new TextRendererV2(),
        };
    }

    public renderLayer(
        context: IRenderContext,
        layer: Layer,
        bounds: IRectangle,
        options: IRenderDocumentOptions,
    ): void {
        context.save();
        try {
            this.applyLayerTransformations(context, layer, options);

            if (isTextLayer(layer)) {
                this.renderTextLayer(
                    context,
                    layer,
                    options.substituteText,
                    options.customFonts,
                    pick(['useTextRenderingLineHeightFix', 'useOldTextRenderingLineHeightFix'], options),
                    options.textToFallbackFontLink,
                );
            } else if (isImageLayer(layer)) {

                const imageSource = options.imageLayerToImageSource
                    ? options.imageLayerToImageSource(layer)
                    : layer.location;
                this.renderImageLayer(context, layer, imageSource, bounds, options.renderPlaceHolderOverlay);
            } else if (isVideoLayer(layer)) {
                this.renderVideoLayer(
                    context,
                    layer,
                    bounds,
                    options.videoLayerToVideoSource,
                    options.renderPlaceHolderOverlay,
                );
            } else if (isMapLayer(layer)) {

                const imageSource = options.imageLayerToImageSource
                    ? options.imageLayerToImageSource(layer as any)
                    : layer.location;
                this.renderMapLayer(
                    context,
                    layer,
                    imageSource,
                    bounds,
                    options.useTextRenderingLineHeightFix,
                    options.useOldTextRenderingLineHeightFix,
                    options.renderPlaceHolderOverlay,
                );
            } else if (layer.type === LayerType.ellipse) {
                this.renderEllipseLayer(context, layer, bounds);
            } else {
                this.renderRectLayer(context, layer, bounds);
            }
        } finally {
            context.restore();
        }
    }

    public renderTemplateBackground(options: IRenderDocumentOptions, bleedBox: IRectangle): void {
        let backgroundColour;
        if (options.asVideoOverlay === true) {
            backgroundColour = BuilderDocumentRenderer.RGBA_TRANSPARENT;
        } else if (
            options.replaceTransparentWithWhite &&
            options.document.background === BuilderDocumentRenderer.RGBA_TRANSPARENT
        ) {
            backgroundColour = BuilderDocumentRenderer.RGB_WHITE;
        } else {
            backgroundColour = options.document.background || BuilderDocumentRenderer.RGB_WHITE;
        }
        options.context.save();
        try {
            options.context.beginPath().rect(bleedBox).fill(backgroundColour);
        } finally {
            options.context.restore();
        }
    }

    // eslint-disable-next-line max-statements
    public renderDocument(options: IRenderDocumentOptions): void {
        const { context } = options;

        const bleedInPixels = this.getBleedInPixels(options);

        const bleedBox = this.getBleedBox(options);
        const trimBox = this.getTrimBox(options);

        context.save();
        try {
            const clipBox = options.clipToTrimBox ? trimBox : bleedBox;

            this.applyGlobalTransform(context, options);

            context.save();
            try {
                context.beginPath().rect(clipBox).clip();

                this.renderTemplateBackground(options, bleedBox);

                this.applyTrimTransform(context, options.trimTransform, options.margin, bleedInPixels);

                const layersToRender = options.document.layers.filter(layer => this.shouldRenderLayer(layer, options));

                for (const layer of layersToRender) {
                    const bounds = this.getLayerBounds(context, layer);
                    this.renderLayer(context, layer, bounds, options);
                }
            } finally {
                context.restore();
            }

            if (options.includeBleed && bleedInPixels && options.includeTrimMarks) {
                this.renderTrimMarks(options.context, options);
            }
        } finally {
            context.restore();
        }
    }

    public calculateSizes(
        builderDocument: BuilderDocument,
        options: ICalculateSizesOptions = {},
    ): ICalculateSizesResult {
        const globalScale = { x: 1, y: 1 };
        const bleedScale = isNullOrUndefined(options.bleedScale) ? 1 : options.bleedScale;
        const bleedInPixels = getBleedAmount(builderDocument) * bleedScale;
        const dimensions = options.dimensions;

        let canvasSize: ISize = { height: 0, width: 0 };

        if (dimensions) {
            globalScale.x = dimensions.width / builderDocument.dimensions.width;
            globalScale.y = dimensions.height / builderDocument.dimensions.height;

            canvasSize = {
                height: dimensions.height + bleedInPixels * 2 * globalScale.y,
                width: dimensions.width + bleedInPixels * 2 * globalScale.x,
            };
        } else {
            canvasSize = {
                height: builderDocument.dimensions.height + bleedInPixels * 2,
                width: builderDocument.dimensions.width + bleedInPixels * 2,
            };
        }

        if (isPrintDocument(builderDocument) && isPageRotated(builderDocument, options.pageIndex || 0)) {
            canvasSize = swapWidthAndHeight(canvasSize);
        }

        return {
            bleedScale,
            canvasSize,
            globalScale,
        };
    }

    public renderDocumentForScreen(
        context: IRenderContext,
        builderDocument: BuilderDocument,
        sizes: ICalculateSizesResult,
        options: IRenderDocumentForScreenOptions,
        customFonts: BuilderFontCustomDto[],
    ): void {
        const globalTransform = { scale: sizes.globalScale };
        const trimTransform = { scale: { x: 1, y: 1 } };

        this.renderDocument({
            asVideoOverlay: options.asVideoOverlay,
            bleedScale: sizes.bleedScale,
            clipToTrimBox: isNullOrUndefined(options.clipToTrimBox) ? true : options.clipToTrimBox,
            context,
            customFonts,
            document: builderDocument,
            globalTransform,
            imageLayerToImageSource: options.imageLayerToImageSource,
            includeBleed: builderDocument.format === BuilderDocumentFormat.print,
            includeTrimMarks: false,
            margin: 0,
            multiImageIndex: options.multiImageIndex,
            outputDimensions: options.dimensions,
            pageIndex: options.pageIndex || undefined,
            renderPlaceHolderOverlay: options.renderPlaceHolderOverlay,
            replaceTransparentWithWhite: options.replaceTransparentWithWhite,
            substituteText: options.substituteText,
            trimTransform,
            useOldTextRenderingLineHeightFix: builderDocument.useOldTextRenderingLineHeightFix,
            useTextRenderingLineHeightFix: builderDocument.useTextRenderingLineHeightFix,
            videoLayerToVideoSource: options.videoLayerToVideoSource,
        });
    }

    /**
     * DSMA-108: iOS renders the first video frame correctly, but not other frames.
     * Expose this method so that the mobile app can override it.
     *
     * @param _video - LoadedVideo
     * @returns boolean
     */
    protected shouldTransformVideo(_video: LoadedVideo): boolean {
        return true;
    }

    /**
     * DSMA-108: iOS rotated videos have weird dimensions. We can hack it by preserving "height".
     * Expose this method so that the mobile app can override it.
     *
     * @param bounds - IRectangle
     * @param is90DegreesRotation - boolean
     * @returns ISize
     */
    protected getRotatedVideoBounds(bounds: IRectangle, is90DegreesRotation: boolean): ISize {
        return {
            height: is90DegreesRotation ? bounds.width : bounds.height,
            width: is90DegreesRotation ? bounds.height : bounds.width,
        };
    }

    private getBleedInPixels(options: IRenderDocumentOptions): number {
        const bleedScale = isNullOrUndefined(options.bleedScale) ? 1 : options.bleedScale;
        let bleed = options.includeBleed ? getBleedAmount(options.document) : 0;
        bleed *= bleedScale;
        return bleed;
    }

    private swapWidthAndHeightIfPageRotated(dimensions: ISize, options: IRenderDocumentOptions): ISize {
        let newDimensions = dimensions;
        if (isPrintDocument(options.document) && isPageRotated(options.document, options.pageIndex || 0)) {
            newDimensions = swapWidthAndHeight(dimensions);
        }
        return newDimensions;
    }

    private getBleedBox(options: IRenderDocumentOptions): IRectangle {
        // We need to apply trim scaling to trim width and trim height only (not trim position)
        const trimScale = this.getTrimScale(options);

        const marginInPixels = options.margin;
        const bleedInPixels = this.getBleedInPixels(options);
        let sizeInPixels = convertUnits(
            options.document.dimensions,
            options.document.dimensions.unit as any,
            PrintUnits.pixels,
        );
        sizeInPixels = scale(sizeInPixels, trimScale);
        sizeInPixels = this.swapWidthAndHeightIfPageRotated(sizeInPixels, options);

        const result = {
            height: sizeInPixels.height + bleedInPixels * 2,
            width: sizeInPixels.width + bleedInPixels * 2,
            x: marginInPixels,
            y: marginInPixels,
        };

        return result;
    }

    private getTrimScale(options: IRenderDocumentOptions): IPoint {

        const x = (options.trimTransform && options.trimTransform.scale && options.trimTransform.scale.x) || 1;

        const y = (options.trimTransform && options.trimTransform.scale && options.trimTransform.scale.y) || 1;
        return { x, y };
    }

    private getTrimBox(options: IRenderDocumentOptions): IRectangle {
        // We need to apply trim scaling to trim width and trim height only (not trim position)
        const trimScale = this.getTrimScale(options);

        const marginInPixels = options.margin;
        const bleedInPixels = this.getBleedInPixels(options);
        let sizeInPixels = convertUnits(
            options.document.dimensions,
            options.document.dimensions.unit as any,
            PrintUnits.pixels,
        );
        sizeInPixels = scale(sizeInPixels, trimScale);
        sizeInPixels = this.swapWidthAndHeightIfPageRotated(sizeInPixels, options);

        const result = {
            height: sizeInPixels.height,
            width: sizeInPixels.width,
            x: marginInPixels + bleedInPixels,
            y: marginInPixels + bleedInPixels,
        };

        return result;
    }

    private shouldRenderLayer(layer: Layer, options: IRenderDocumentOptions) {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
        if (layer.visible === false) {
            return false;
        } else if (isVideoLayer(layer) && options.asVideoOverlay) {
            return false;
        }

        const doc = options.document;

        if (isPrintDocument(doc)) {
            const pageIndex = options.pageIndex || 0;
            const page = doc.pages[pageIndex];
            return page.layerIds.includes(layer.id);
        } else if (isMultiImageContentDocument(doc)) {
            const multiImageIndex = options.multiImageIndex || 0;
            const image = doc.multiImage[multiImageIndex];
            return image.layerIds.includes(layer.id);
        }

        return true;
    }

    private getLayerBounds(context: IRenderContext, layer: Layer) {
        const contextSize = context.getSize();

        const bounds = {
            height: layer.height,
            width: layer.width,
            x: layer.position.x + (contextSize.width / 2 - layer.width / 2),
            y: layer.position.y + (contextSize.height / 2 - layer.height / 2),
        };

        return bounds;
    }

    private renderTrimMarks(context: IRenderContext, options: IRenderDocumentOptions) {
        const bleed = this.getBleedInPixels(options);
        const padding = options.margin * 2 + bleed * 2;

        const trimScale = this.getTrimScale(options);

        const trimSize = scale(options.document.dimensions, trimScale);

        let pageSize = {
            height: trimSize.height + padding,
            width: trimSize.width + padding,
        };

        pageSize = this.swapWidthAndHeightIfPageRotated(pageSize, options);

        const trimLineWidth = convertUnits(0.2, PrintUnits.millimetres, PrintUnits.points);

        const trimLineRects = this.getTrimLineRects({
            margin: options.margin,
            pageSize,
            trim: bleed,
            trimLineLength: options.margin - trimLineWidth,
            trimLineWidth,
        });

        for (const rect of trimLineRects) {
            context.beginPath().rect(rect).lineWidth(trimLineWidth).fillAndStroke('#000', '#fff');
        }
    }

    private getTrimLineRects(options: ITrimLineRectsOptions) {
        const corners = getEnumValues(RectangleCorner);
        const orientations = getEnumValues(TrimLineOrientation);

        return corners
            .map(corner =>
                orientations.map(orientation => ({
                    corner,
                    orientation,
                })),
            )
            .reduce((result, current) => result.concat(current), [])
            .map(cornerAndOrientation =>
                this.getTrimLineRect(cornerAndOrientation.corner, cornerAndOrientation.orientation, options),
            );
    }

    private getTrimLineRect(
        corner: RectangleCorner,
        orientation: TrimLineOrientation,
        options: ITrimLineRectsOptions,
    ): IRectangle {
        const { margin, pageSize, trim, trimLineLength, trimLineWidth } = options;


        if (orientation !== TrimLineOrientation.horizontal && orientation !== TrimLineOrientation.vertical) {
            throw new Error(`Unknown Orientation: ${orientation}`);
        }

        switch (corner) {
            case RectangleCorner.topLeft:
                return orientation === TrimLineOrientation.horizontal
                    ? { height: trimLineWidth * 3, width: trimLineLength, x: 0, y: margin + trim - trimLineWidth }
                    : { height: trimLineLength, width: trimLineWidth * 3, x: margin + trim - trimLineWidth, y: 0 };

            case RectangleCorner.topRight:
                return orientation === TrimLineOrientation.horizontal
                    ? {
                          height: trimLineWidth * 3,
                          width: -trimLineLength,
                          x: pageSize.width,
                          y: margin + trim - trimLineWidth,
                      }
                    : {
                          height: trimLineLength,
                          width: trimLineWidth * 3,
                          x: pageSize.width - margin - trim - trimLineWidth,
                          y: 0,
                      };

            case RectangleCorner.bottomRight:
                return orientation === TrimLineOrientation.horizontal
                    ? {
                          height: trimLineWidth * 3,
                          width: trimLineLength,
                          x: pageSize.width - trimLineLength,
                          y: pageSize.height - margin - trim - trimLineWidth,
                      }
                    : {
                          height: trimLineLength,
                          width: trimLineWidth * 3,
                          x: pageSize.width - margin - trim - trimLineWidth,
                          y: pageSize.height - trimLineLength,
                      };

            case RectangleCorner.bottomLeft:
                return orientation === TrimLineOrientation.horizontal
                    ? {
                          height: trimLineWidth * 3,
                          width: trimLineLength,
                          x: 0,
                          y: pageSize.height - margin - trim - trimLineWidth,
                      }
                    : {
                          height: trimLineLength,
                          width: trimLineWidth * 3,
                          x: margin + trim - trimLineWidth,
                          y: pageSize.height - trimLineLength,
                      };

            default:
                throw new Error(`Unknown Corner Type: ${corner}`);
        }
    }

    private renderImageLayer(
        context: IRenderContext,
        layer: ImageLayer,
        imageSource: any,
        bounds: IRectangle,
        renderPlaceHolderOverlay?: RenderPlaceholderOverlayCallback,
    ) {
        if (imageSource) {
            context.image(imageSource, { x: 0, y: 0 }, bounds);
        }

        if (layer.showPlaceholderOverlay && renderPlaceHolderOverlay) {
            renderPlaceHolderOverlay(context, layer);
        }
    }

    // eslint-disable-next-line max-params
    private renderMapLayer(
        context: IRenderContext,
        layer: MapLayer,
        imageSource: any,
        bounds: IRectangle,
        useTextRenderingLineHeightFix: boolean,
        useOldTextRenderingLineHeightFix: boolean,
        renderPlaceHolderOverlay?: RenderPlaceholderOverlayCallback,
    ) {
        if (imageSource) {
            context.image(imageSource, { x: 0, y: 0 }, bounds);
        }
        if (layer.showPlaceholderOverlay && renderPlaceHolderOverlay) {
            renderPlaceHolderOverlay(context, layer);
        } else if (layer.displayPin) {
            this.renderMapPin(context, layer, useTextRenderingLineHeightFix, useOldTextRenderingLineHeightFix);
        }
    }

    private renderMapPin(
        context: IRenderContext,
        layer: MapLayer,
        useTextRenderingLineHeightFix: boolean,
        useOldTextRenderingLineHeightFix: boolean,
    ) {
        const iconFontFamily = 'Font Awesome 5 Pro';
        const icon = String.fromCharCode(0xf3c5);
        const fontSizePixels = convertUnits(20, PrintUnits.points, PrintUnits.pixels);
        const MAGIC_NUMBER = 0.00666666666;
        const iconMultiply = layer.width * MAGIC_NUMBER;
        const iconFontSizePixels = fontSizePixels * iconMultiply;
        context.fontFamily(iconFontFamily).fontSize(iconFontSizePixels, 'px').fontWeight('900');
        const iconWidth = context.textWidth(icon);
        context.fillColor(layer.pinFill);
        context.text({
            fill: true,
            position: {
                x: layer.width / 2 - Math.floor(iconWidth / 2),
                y: this.calculateMapPinYPosition(
                    layer.height,
                    iconFontSizePixels,
                    useTextRenderingLineHeightFix,
                    useOldTextRenderingLineHeightFix,
                ),
            },
            stroke: false,
            text: icon,
        });
    }

    private calculateMapPinYPosition(
        layerHeight: number,
        iconFontSizePixels: number,
        useTextRenderingLineHeightFix: boolean,
        useOldTextRenderingLineHeightFix: boolean,
    ): number {
        let verticalAdjustment: number;
        if (useTextRenderingLineHeightFix) {
            verticalAdjustment = iconFontSizePixels / 8; // 8 seems to be the magic number to correctly align the icon
        } else if (useOldTextRenderingLineHeightFix) {
            verticalAdjustment = iconFontSizePixels / 2;
        } else {
            verticalAdjustment = iconFontSizePixels;
        }
        return layerHeight / 2 - verticalAdjustment;
    }

    private renderEllipseLayer(context: IRenderContext, layer: Layer, size: ISize) {
        let radiusX = Math.abs(size.width / 2);
        let radiusY = Math.abs(size.height / 2);
        const x = size.width / 2;
        const y = size.height / 2;

        if (layer.strokeWidth > 0) {
            radiusX -= layer.strokeWidth / 2;
            radiusY -= layer.strokeWidth / 2;
        }

        if (radiusX <= 0 || radiusY <= 0) {
            return;
        }

        context.beginPath().ellipse({
            endAngle: 2 * Math.PI,
            radiusX,
            radiusY,
            rotation: 0,
            startAngle: 0,
            x,
            y,
        });
        this.applyLayerFillAndStroke(context, layer);
    }

    private renderRectLayer(context: IRenderContext, layer: Layer, size: ISize) {
        context.beginPath().rect({ height: size.height, width: size.width, x: 0, y: 0 });
        this.applyLayerFillAndStroke(context, layer);
    }

    // eslint-disable-next-line max-params
    private renderTextLayer(
        context: IRenderContext,
        layer: TextLayer,
        substituteText: (text: string) => string,
        customFonts: BuilderFontCustomDto[],
        textRenderingFlags: { useTextRenderingLineHeightFix: boolean; useOldTextRenderingLineHeightFix: boolean },
        textToFallbackFontLink?: TextToFallbackFontLink[],
    ) {
        const renderer = layer.textFit === TextFit.shrinkToBounds ? this.textRenderer.v1 : this.textRenderer.v2;
        renderer.renderText({
            context,
            customFonts,
            layer,
            substituteText,
            textToFallbackFontLink,
            ...textRenderingFlags,
        });
    }

    private renderVideoLayer(
        context: IRenderContext,
        layer: VideoLayer,
        bounds: IRectangle,
        videoLayerToVideoSource: (layer: VideoLayer) => LoadedVideo,
        renderPlaceHolderOverlay?: RenderPlaceholderOverlayCallback,
    ) {
        if (layer.location) {
            const video: LoadedVideo = videoLayerToVideoSource(layer);

            if (video) {
                context.save();
                let transformMatrix: AffineTransform | undefined;
                if (video.preferredTransform) {
                    // DSMA-108: iOS: video orientation is not respected when drawing to the canvas in iOS WebView
                    transformMatrix = video.preferredTransform;

                } else if (video.orientation && video.orientation === VideoOrientation.Rotation90) {
                    /**
                     * DS-1947: video orientation metadata is not respected when drawing to the canvas in
                     * Firefox or Safari.
                     */
                    /* eslint-disable id-length */
                    transformMatrix = {
                        // 90
                        a: 0,
                        b: 1,
                        c: -1,
                        d: 0,
                        tx: layer.width,
                        ty: 0,
                    };
                    /* eslint-enable id-length */
                }
                if (transformMatrix && this.shouldTransformVideo(video)) {
                    const translation = {
                        x: transformMatrix.tx ? layer.width : 0,
                        y: transformMatrix.ty ? layer.height : 0,
                    };
                    context.transform(
                        transformMatrix.a,
                        transformMatrix.b,
                        transformMatrix.c,
                        transformMatrix.d,
                        translation.x,
                        translation.y,
                    );
                    const is90DegreesRotation = !!(Math.abs(transformMatrix.b) && Math.abs(transformMatrix.c));
                    context.image(
                        video.frame,
                        {
                            x: 0,
                            y: 0,
                        },
                        this.getRotatedVideoBounds(bounds, is90DegreesRotation),
                    );
                } else {
                    context.image(video.frame, { x: 0, y: 0 }, bounds);
                }
                context.restore();
            }
        } else if (renderPlaceHolderOverlay) {
            renderPlaceHolderOverlay(context, layer);
        }
    }

    private applyLayerFillAndStroke(context: IRenderContext, layer: Layer) {
        context.lineWidth(layer.strokeWidth);

        if (layer.stroke && layer.strokeWidth && layer.fill) {
            context.fillAndStroke(layer.fill, layer.stroke);
        } else if (layer.fill) {
            context.fill(layer.fill);
        } else if (layer.strokeWidth) {
            context.stroke(layer.stroke);
        }
    }

    // eslint-disable-next-line max-statements
    private applyLayerTransformations(context: IRenderContext, layer: Layer, options: IRenderDocumentOptions) {
        const document = options.document;
        let documentSize = document.dimensions as ISize;

        if (isPrintDocument(document) && isPageRotated(document, options.pageIndex || 0)) {
            documentSize = swapWidthAndHeight(documentSize);
        }

        /*
         * Const halfDocWidth = Math.floor(documentSize.width / 2);
         * Const halfDocHeight = Math.floor(documentSize.height / 2);
         */
        const halfDocWidth = documentSize.width / 2;
        const halfDocHeight = documentSize.height / 2;

        const layerBounds = {
            height: layer.height,
            width: layer.width,
            x: isNaN(layer.position.x) ? 0 : Math.floor(layer.position.x),
            y: isNaN(layer.position.y) ? 0 : Math.floor(layer.position.y),
        };

        const offsetX = halfDocWidth + layerBounds.x;
        const offsetY = halfDocHeight + layerBounds.y;

        // Translate
        context.translate({ x: offsetX, y: offsetY });

        const halfLayerWidth = layer.width / 2;
        const halfLayerHeight = layer.height / 2;

        // Rotation
        const radians = degreesToRadians(layer.rotation);
        context.rotate(radians);

        // Skew
        context.transform(1, layer.skewX, layer.skewY, 1, 0, 0);

        // Scale
        context.scale({ x: layer.scale, y: layer.scale });

        context.translate({ x: -halfLayerWidth, y: -halfLayerHeight });

        context.opacity(layer.opacity);

        if (layer.blendMode) {
            context.compositeOperation(layer.blendMode);
        }

        if (!isTextLayer(layer) || document.clipTextLayers) {
            context.beginPath().rect({ height: layer.height, width: layer.width, x: 0, y: 0 }).clip();
        }
    }

    private applyGlobalTransform(context: IRenderContext, options: IRenderDocumentOptions) {

        if (options.globalTransform && options.globalTransform.scale) {
            context.scale(options.globalTransform.scale);
        }
    }

    private applyTrimTransform(
        context: IRenderContext,
        trimTransform: { scale: IPoint },
        margin: number,
        bleedAmount: number,
    ) {
        context.translate({ x: margin + bleedAmount, y: margin + bleedAmount });


        if (trimTransform && trimTransform.scale) {
            context.scale(trimTransform.scale);
        }
    }
}
